import type { ReactNode } from 'react';
import { useCanAccess } from 'react-admin';
import { resources } from '@repo/c360-ra-components';

/** Resources for each collapsible section - show section header if user has access to ANY */
export const SECTION_RESOURCES = {
  clientUsers: [resources.clientUsers, resources.signinAttemptHistoryLogs, resources.guardianCustomers, resources.blacklistRecords, resources.maxSignupRecords, resources.signupAttemptHistoryLogs, resources.manualMerges],
  paymentsAndRenewals: [resources.taReports, resources.orderRecords, resources.nonOnlineRenewals, resources.manualPaymentExecutionLogs, resources.bankCards, resources.facRecords],
  recurringPayments: [resources.recurringPayments, resources.paymentCards, resources.recurringPaymentsExecutionLogs],
  applicationManagement: [resources.customerApplications, resources.applicants, resources.riskProfiles, resources.documents, resources.docusign],
  salespalApplications: [resources.salespalApplications],
  manualMerges: [resources.manualMerges],
} as const;

const MAX_RESOURCES = 9;

type CanAccessAnyProps = {
  resources: readonly string[];
  action?: 'list' | 'show' | 'create' | 'edit' | 'delete';
  children: ReactNode;
};

/**
 * Renders children only if the user has access to at least one of the given resources.
 * Use for collapsible section headers so the section shows when any nested item is accessible.
 */
export const CanAccessAny = ({ resources: resourceList, action = 'list', children }: CanAccessAnyProps) => {
  const list = [...resourceList];
  const padded = [...list, ...Array(Math.max(0, MAX_RESOURCES - list.length)).fill(list[0])] as string[];

  const c1 = useCanAccess({ resource: padded[0], action });
  const c2 = useCanAccess({ resource: padded[1], action });
  const c3 = useCanAccess({ resource: padded[2], action });
  const c4 = useCanAccess({ resource: padded[3], action });
  const c5 = useCanAccess({ resource: padded[4], action });
  const c6 = useCanAccess({ resource: padded[5], action });
  const c7 = useCanAccess({ resource: padded[6], action });
  const c8 = useCanAccess({ resource: padded[7], action });
  const c9 = useCanAccess({ resource: padded[8], action });
  const canAccess = c1.canAccess || c2.canAccess || c3.canAccess || c4.canAccess || c5.canAccess || c6.canAccess || c7.canAccess || c8.canAccess || c9.canAccess;

  if (!canAccess) return null;
  return <>{children}</>;
};
